import { effect, signal } from "@preact/signals";

import * as analytics from "analytics";
import * as haptics from "haptics";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import { TypedEventEmitter } from "shared/TypedEventEmitter";

export interface TodayStats {
  goalMet: boolean;
  fractionOfGoalMet: number;
  wordsRead: number;
  wordsPracticed: number;
  byLanguage: {
    [language: string]: {
      wordsRead: number;
      wordsPracticed: number;
    };
  };
}

export interface StreakData {
  days: number;
  repairableDays: number;
  todayStats: TodayStats;
}

export default class StreakStore {
  readonly data = signal<StreakData | null>(null);

  /** Fires when today's goal transitions from unmet to met within a session. */
  readonly events = new TypedEventEmitter<{ goalMet: [] }>();

  private lastFetchTime = 0;
  private readonly minimumRefetchInterval = 5 * 60 * 1000;
  private readonly normalRefetchInterval = 60 * 60 * 1000;
  private intervalID: ReturnType<typeof setInterval> | undefined;
  private timeoutID: ReturnType<typeof setTimeout> | undefined;
  private visibilityChangeListener: () => void;
  private disposeGoalMetEffect: (() => void) | undefined;

  get days() {
    return this.data.value?.days;
  }
  get repairableDays() {
    return this.data.value?.repairableDays;
  }
  get todayStats() {
    return this.data.value?.todayStats;
  }
  get hasData() {
    return this.data.value !== null;
  }

  constructor() {
    this.intervalID = setInterval(
      () => void this.refetchIfAppropriate(),
      this.normalRefetchInterval
    );

    this.visibilityChangeListener = () => {
      void this.refetchIfAppropriate();
    };
    document.addEventListener(
      "visibilitychange",
      this.visibilityChangeListener
    );

    // Track goal-met transition: false → true
    let previousGoalMet: boolean | undefined;
    this.disposeGoalMetEffect = effect(() => {
      const current = this.data.value;
      if (current === null) {
        previousGoalMet = undefined;
        return;
      }
      const currentGoalMet = current.todayStats.goalMet;
      if (previousGoalMet === false && currentGoalMet === true) {
        analytics.trackEventWithProperties("streakGoalMet", {
          days: current.days,
          wordsRead: current.todayStats.wordsRead,
          wordsPracticed: current.todayStats.wordsPracticed,
        });
        this.events.trigger("goalMet");
        haptics.play("success");
      }
      previousGoalMet = currentGoalMet;
    });
  }

  async fetch() {
    const { data } = await readlangClient.request<StreakData>(
      "GET",
      "/api/userLearningLog/streak",
      {}
    );
    this.data.value = data;
    this.lastFetchTime = Date.now();
  }

  /**
   * Logs words practiced towards today's goal, then refetches so the streak
   * shown (web header, iOS native top bar) includes them
   */
  async logPracticed(wordsPracticed: number, language: string | undefined) {
    await readlangClient.request("POST", "/api/userLearningLog/practiced", {
      wordsPracticed,
      language,
      timezoneOffset: dateUtils.getTimezoneOffset(),
    });
    if (!(await readlangClient.isLocalMode())) {
      await this.fetch();
    }
  }

  /**
   * Refetches unless hidden, signed out or fetched in the last few minutes,
   * e.g. on mounting a streak view and on page changes (the header's streak
   * view stays mounted across them)
   */
  async refetchIfAppropriate() {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
      this.timeoutID = undefined;
    }

    if (document.visibilityState !== "visible") {
      return;
    }

    if (await readlangClient.isLocalMode()) {
      return;
    }

    const timeSinceLastFetch = Date.now() - this.lastFetchTime;
    if (timeSinceLastFetch > this.minimumRefetchInterval) {
      // Before the request, so calls made while it's in flight skip it
      this.lastFetchTime = Date.now();
      try {
        await this.fetch();
      } catch {
        // Network errors during background refetch are non-critical
      }

      this.timeoutID = setTimeout(
        () => void this.refetchIfAppropriate(),
        this.normalRefetchInterval
      );
    }
  }

  dispose() {
    if (this.intervalID) {
      clearInterval(this.intervalID);
      this.intervalID = undefined;
    }
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
      this.timeoutID = undefined;
    }
    document.removeEventListener(
      "visibilitychange",
      this.visibilityChangeListener
    );
    this.disposeGoalMetEffect?.();
  }
}
